// File: src/pages/SessionDetail.jsx
import React, { useMemo, useContext } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Text,
  SkeletonPage,
  SkeletonBodyText,
  Banner,
  TextContainer,
  List,
  InlineStack,
  Button,
  Collapsible,
  Box,
} from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import { SiteContext } from '../context/SiteContext';
import LayoutWrapper from '../components/Layout/Layout';
import useData from '../hooks/useData';

export default function SessionDetail() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const urlSiteId = searchParams.get('site_id');
  const { token } = useContext(AuthContext);
  const { activeSite } = useContext(SiteContext);
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
  const navigate = useNavigate();

  // Tracks which dates are expanded in the timeline
  const [expandedDates, setExpandedDates] = React.useState({});

  const siteId = useMemo(() => urlSiteId || activeSite?.id, [urlSiteId, activeSite]);

  // Use the useData hook to fetch and cache session details
  const { data: events, loading, error } = useData(
    siteId ? `sessionDetail:${sessionId}:${siteId}` : 'noop',
    async () => {
      if (!siteId) {
        return Promise.reject('No site selected');
      }

      const url = `${API}/sessions/${encodeURIComponent(sessionId)}?site_id=${encodeURIComponent(siteId)}`;

      try {
        const res = await fetch(url, {
          headers: {
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (!res.ok) {
          let detail = await res.text();
          try { detail = JSON.parse(detail).detail; } catch {}
          throw new Error(`Error ${res.status}${detail ? `: ${detail}` : ''}`);
        }
        return await res.json();
      } catch (e) {
        throw e;
      }
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [API, token, sessionId, siteId] }
  );

  const safeEvents = useMemo(() => events || [], [events]);

  // Hide non-final minute heartbeats from the timeline table
  const displayEvents = useMemo(() => {
    return (safeEvents || []).filter(e => !(e.type === 'page_duration' && !e.final));
  }, [safeEvents]);

  // 1) Session summary (entry/exit, duration, counts)
  const summary = useMemo(() => {
    if (!safeEvents.length) return null;

    const startEvt = safeEvents.find(e => e.type === 'session_start') || safeEvents[0];
    const endEvt   = [...safeEvents].reverse().find(e => e.type === 'session_end') || safeEvents[safeEvents.length - 1];
    const seconds  = Math.max(0, (new Date(endEvt.timestamp) - new Date(startEvt.timestamp)) / 1000);

    const pageviews   = safeEvents.filter(e => e.type === 'pageview').length;
    const clicks      = safeEvents.filter(e => e.type === 'click').length;
    const scrolls     = safeEvents.filter(e => e.type === 'scroll_depth').length;
    const checkouts   = safeEvents.filter(e => e.type === 'checkout_start' || e.type === 'checkout_started').length;
    const purchases   = safeEvents.filter(e => e.type === 'purchase' || e.type === 'order_completed');
    const purchaseCnt = purchases.length;
    const revenue     = purchases.reduce((sum, e) => sum + (e.value || 0), 0);

    const firstPV = safeEvents.find(e => e.type === 'pageview');
    const lastPage = endEvt.page || safeEvents[safeEvents.length - 1].page;

    return {
      entry: firstPV?.page || 'unknown',
      exit: lastPage,
      duration: `${Math.floor(seconds/60)}m ${Math.floor(seconds%60)}s`,
      pageviews, clicks, scrolls, checkouts,
      purchaseCount: purchaseCnt,
      revenue: revenue.toFixed(2),
      totalSeconds: seconds,
    };
  }, [safeEvents]);

  // 2) Page-Level Breakdown (duration between pageviews; last page uses session end)
  const pageBreakdown = useMemo(() => {
    if (!safeEvents.length) return [];

    const pageData = {};
    const pvs = safeEvents
      .filter(e => e.type === 'pageview')
      .sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));

    for (let i = 0; i < pvs.length; i++) {
      const cur = pvs[i];
      const next = pvs[i+1];
      const page = cur.page || 'unknown';
      const start = new Date(cur.timestamp).getTime();
      const end = next
        ? new Date(next.timestamp).getTime()
        : new Date(safeEvents[safeEvents.length - 1].timestamp).getTime();

      if (!pageData[page]) pageData[page] = { visits: 0, clicks: 0, scrolls: 0, totalMs: 0 };
      pageData[page].visits += 1;
      pageData[page].totalMs += Math.max(0, end - start);
    }

    // count clicks/scrolls per page only if that page had a pageview
    safeEvents.forEach(evt => {
      const page = evt.page || 'unknown';
      if (!pageData[page]) return;
      if (evt.type === 'click') pageData[page].clicks += 1;
      if (evt.type === 'scroll_depth') pageData[page].scrolls += 1;
    });

    return Object.entries(pageData)
      .map(([page, d]) => {
        const secs = Math.floor(d.totalMs / 1000);
        const mm = Math.floor(secs / 60);
        const ss = secs % 60;
        return [page, d.visits, d.clicks, d.scrolls, `${mm}m ${ss}s`];
      })
      .sort((a,b) => b[1] - a[1]); // by visits desc
  }, [safeEvents]);


  // 3) Group events by calendar date
  const groupedEvents = useMemo(() => {
    const groups = {};
    displayEvents.forEach(evt => {
      const dateKey = new Date(evt.timestamp).toLocaleDateString();
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(evt);
    });
    return groups;
  }, [displayEvents]);

  // 5) “AI Insight” heuristics with severity grouping
const insightBlock = useMemo(() => {
  if (!summary) return null;
  const insights = [];

  // Summary of what happened
  insights.push({
    severity: 'info',
    title: `This visitor viewed ${summary.pageviews} pages in ${summary.duration}, clicked ${summary.clicks} times, and ${summary.checkouts > 0 ? 'started checkout' : 'never started checkout'}.`,
    description: null,
    recommendations: [],
  });

  // Abandoned checkout
  if (summary.checkouts > 0 && summary.purchaseCount === 0) {
    insights.push({
      severity: 'critical',
      title: 'Checkout was started but not completed.',
      recommendations: [
        'Test your checkout flow for slow load times or bugs on mobile.',
        'Make sure shipping costs are shown clearly before checkout.',
        'Enable abandoned cart emails with discount offers for incomplete purchases.',
      ],
    });
  }

  // Bounce
  if (summary.entry === summary.exit && summary.totalSeconds < 30) {
    insights.push({
      severity: 'warning',
      title: 'User bounced shortly after landing.',
      recommendations: [
        'Add a clear CTA like “Browse Bestsellers” or “Start Shopping” above the fold.',
        'Check your first paint speed using PageSpeed Insights and improve load performance.',
        'Use a hero image or headline that clearly states what your store sells.',
      ],
    });
  }

  // Engaged session but no funnel entry
  if (summary.pageviews > 5 && summary.checkouts === 0 && summary.purchaseCount === 0) {
    insights.push({
      severity: 'opportunity',
      title: 'User viewed several pages but didn’t view any products or add items to cart.',
      recommendations: [
        'Add featured products to your homepage so users immediately see items for sale.',
        'Ensure your menu includes “Shop All” or “Collections” links at the top.',
        'Test adding a “Shop Now” button on your banner or hero section.',
      ],
    });
  }

  // Low click-to-pageview
  if (summary.clicks / Math.max(summary.pageviews, 1) < 0.1) {
    insights.push({
      severity: 'warning',
      title: 'Low click-to-pageview ratio—users may not know where to go next.',
      recommendations: [
        // 'Add directional cues like arrows, scroll hints, or labels like “See more” under product sections.',
        // 'Limit homepage content to 1–2 clear sections that link directly to products.',
        // 'Make buttons larger and more visible, especially on mobile.',
      ],
    });
  }

  // Long single-page session (likely bot or stuck)
  if (summary.totalSeconds > 900 && summary.pageviews < 5) {
    insights.push({
      severity: 'critical',
      title: 'Single-page session lasting over 15 minutes—possible bot or stuck user.',
      recommendations: [
        // 'Use CAPTCHA or challenge pages on landing to filter out bots.',
        // 'Check for JavaScript errors that might prevent navigation (e.g., console logs, broken links).',
        // 'Review heatmaps or session recordings for this page to identify user confusion or traps.',
      ],
    });
  }

  // No funnel engagement at all
  const viewedProduct = safeEvents.some(e => e.page?.includes('/products'));
  if (!viewedProduct && summary.pageviews > 5) {
    insights.push({
      severity: 'critical',
      title: 'No product pages were viewed during this session.',
      recommendations: [
        'Add a “Shop Products” link to your top navigation.',
        'Place 3–4 featured product cards on the homepage with clear images and prices.',
        'Ensure product page links are working and not hidden behind slow-loading elements.',
      ],
    });
  }

  // If no major issues were detected
  const hasOnlyInfo = insights.length === 1;
  if (hasOnlyInfo) {
    insights.push({
      severity: 'opportunity',
      title: 'No major issues flagged—session seems normal.',
      recommendations: [
        'Use this session as a benchmark for comparison—did they click key CTAs or reach product pages?',
        'Look for similar sessions that didn’t convert and spot shared traits (e.g., device type, traffic source).',
        'Run A/B tests to compare versions of key pages for conversion impact.',
      ],
    });
  }

  return insights;
}, [summary, safeEvents]);


  const toggleDate = date => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const handleBackToSessions = () => {
    navigate('/sessions');
  };

  if (loading) {
    return (
      <Page
        title="Session Details"
        backAction={{ content: 'Back to Sessions', onAction: handleBackToSessions }}
      >
        <SkeletonPage title="Session Details">
          <SkeletonBodyText />
        </SkeletonPage>
      </Page>
    );
  }

  if (error) {
    return (
      <Page
        title="Session Details"
        backAction={{ content: 'Back to Sessions', onAction: handleBackToSessions }}
      >
        <Layout>
          <Layout.Section>
            <Banner status="critical">{error}</Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!summary) {
    return (
      <Page
        title="Session Details"
        backAction={{ content: 'Back to Sessions', onAction: handleBackToSessions }}
      >
        <Layout>
          <Layout.Section>
            <Text>No data available for this session.</Text>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <LayoutWrapper>
    <Page
      title="Session Details"
      backAction={{ content: 'Back to Sessions', onAction: handleBackToSessions }}
    >
      <Layout>

        {/* 🔍 AI Insights & Recommendations */}
        <Layout.Section fullWidth>
          <Card title="AI Insights & Recommendations" sectioned>

            {/* “What Happened” one-line summary */}
            {insightBlock
              .filter(ins => ins.severity === 'info' && ins.recommendations.length === 0)
              .map((ins, idx) => (
                <Text key={idx} variation="subdued">
                  {ins.title}
                </Text>
              ))}

            {/* Render each insight in its own Card, grouped by severity */}
            <Box marginBlockStart="4">
              {insightBlock
                .filter(ins => ins.recommendations.length > 0)
                .map((insight, idx) => {
                  let icon = null;
                  if (insight.severity === 'critical') icon = '🚨';
                  if (insight.severity === 'warning') icon = '⚠️';
                  if (insight.severity === 'opportunity') icon = '💡';

                  return (
                    <Card
                      key={idx}
                      title={
                        <Text variation="strong">
                          {icon} {insight.title}
                        </Text>
                      }
                      sectioned
                      plain={false}
                    >
                      <TextContainer>
                        <List type="bullet">
                          {insight.recommendations.map((rec, i) => (
                            <List.Item key={i}>{rec}</List.Item>
                          ))}
                        </List>
                      </TextContainer>
                    </Card>
                  );
                })}
            </Box>

          </Card>
        </Layout.Section>

        {/* 📊 Page-Level Breakdown */}
        <Layout.Section fullWidth>
          <Card title="Page-Level Breakdown" sectioned>
            <DataTable
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text']}
              headings={['Page', 'Visits', 'Clicks', 'Scrolls', 'Duration']}
              rows={pageBreakdown}
              verticalAlign="middle"
            />
          </Card>
        </Layout.Section>

        {/* 🕓 Session Timeline (Grouped by Date) */}
        <Layout.Section fullWidth>
          <Card title="Session Timeline" sectioned>
            {Object.entries(groupedEvents).map(([date, eventsArray]) => (
              <React.Fragment key={date}>
                <InlineStack alignment="space-between" blockAlign="center">
                  {/* Now shows “date – X events” */}
                  <Text variant="headingMd">
                    {date} — {eventsArray.length} events
                  </Text>
                  <Button plain onClick={() => toggleDate(date)}>
                    {expandedDates[date] ? 'Hide Details' : 'Show Details'}
                  </Button>
                </InlineStack>

                <Collapsible
                  open={!!expandedDates[date]}
                  id={`collapse-${date}`}
                  transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}
                >
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Timestamp', 'Event Type', 'Page', 'Notes']}
                    rows={eventsArray.map(evt => [
                      new Date(evt.timestamp).toLocaleString(),
                      evt.type,
                      evt.page || '',
                      evt.description || '',
                    ])}
                    verticalAlign="middle"
                  />
                </Collapsible>
              </React.Fragment>
            ))}
          </Card>
        </Layout.Section>

      </Layout>
    </Page>
    </LayoutWrapper>
  );
}